import { X } from "lucide-react";
import { Dialog as RadixDialog } from "radix-ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";

export type SheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  // Desktop side: right (details) or left (menu). Mobile is always a bottom sheet.
  side?: "right" | "left";
  className?: string;
};

// Bottom sheet on phones, side panel on desktop; built on Radix Dialog for focus handling.
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side = "right",
  className,
}: SheetProps) {
  const { t } = useTranslation("translation", { keyPrefix: "common" });
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-zinc-900/40" />
        <RadixDialog.Content
          // Radix warns without a description; the title alone is fine for these panels.
          {...(description ? {} : { "aria-describedby": undefined })}
          className={cn(
            "fixed z-50 flex flex-col bg-white shadow-xl focus:outline-none",
            "inset-x-0 bottom-0 max-h-[90vh] rounded-t-xl pb-safe",
            "md:inset-y-0 md:max-h-none md:w-[420px] md:max-w-[90vw] md:rounded-none",
            side === "right" ? "md:right-0 md:left-auto" : "md:left-0 md:right-auto",
            className,
          )}
        >
          <div className="flex items-start justify-between gap-3 border-zinc-200 border-b px-5 py-4">
            <div>
              <RadixDialog.Title className="font-semibold text-lg">{title}</RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="mt-1 text-sm text-zinc-500">
                  {description}
                </RadixDialog.Description>
              ) : null}
            </div>
            <RadixDialog.Close asChild>
              <button
                type="button"
                aria-label={t("close")}
                className="-my-3 -mr-3 flex size-11 shrink-0 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100"
              >
                <X className="size-5" aria-hidden />
              </button>
            </RadixDialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer ? (
            <div className="flex flex-wrap justify-end gap-2 border-zinc-200 border-t px-5 py-4">
              {footer}
            </div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
